import { AlternativeInvestmentProductSubType, InvestmentBalanceTypeCode } from '@repo/shared-types'
import { AccountListAccountType } from '../../../list/accountList'

export interface AccountDetailAmountCurrency {
  amount: number
  currency: string
}

export interface AccountDetailPortfolioDistribution extends AccountDetailAmountCurrency {
  id: string
  percentage: number
  percentagePerBar: number
}

export type AccountType = 'AUTOGESTIONADA' | string
export type IntervinentType = 'HOLDER' | string
export type AccountSecuritiesType =
  | 'SECURITIES_ACCOUNT'
  | 'PENSION_PLAN_ACCOUNT'
  | 'CASH_ACCOUNT'
  | 'CREDIT_ACCOUNT'

export interface AccountDetailHeader {
  alias: string | undefined
  totalValuation: AccountDetailAmountCurrency
  totalInvestmentValue: AccountDetailAmountCurrency
  portfolioDistribution: AccountDetailPortfolioDistribution[]
  account: {
    type: AccountType
    iban: string
    externalId: string
    intervinent: {
      name: string
      type: IntervinentType
    }
    accountType: AccountSecuritiesType
  }
}

export interface AccountDetailItem {
  id: string
  isin: string
  name: string
  productSubType?: AlternativeInvestmentProductSubType
  subtitle?: string
  balanceTypeCode?: InvestmentBalanceTypeCode
  valuation: AccountDetailAmountCurrency
  performance?: {
    absolute: number
    relative: number
  }
}

export interface AccountDetailProductSection {
  items: AccountDetailItem[]
}

export const investmentTypes = [
  'funds',
  'etfs',
  'variableIncome',
  'fixedIncome',
  'pensionPlans',
  'epsv',
  'derivatives',
  'warrants',
  'otherProductLifeSavingsInsurance',
  'otherProductGeneralPersonalInsurance',
  'otherProductPensionPlans',
  'otherProductAlternativeInvestments',
  'otherProductSocimis',
] as const

export type InvestmentType = (typeof investmentTypes)[number]

export const externalInvestmentTypes = [
  'otherProductLifeSavingsInsurance',
  'otherProductGeneralPersonalInsurance',
  'otherProductPensionPlans',
  'otherProductAlternativeInvestments',
  'otherProductSocimis',
] as const satisfies readonly InvestmentType[]

export type ExternalInvestmentType = (typeof externalInvestmentTypes)[number]

export const investmentOrderForSecuritiesAccount: Array<InvestmentType> = [
  'funds',
  'etfs',
  'variableIncome',
  'fixedIncome',
  'derivatives',
  'warrants',
]

export const investmentOrderForPensionPlansAccount: Array<InvestmentType> = ['pensionPlans', 'epsv']

export const investmentOrderForCashAccount: Array<InvestmentType> = [
  'otherProductPensionPlans',
  'otherProductAlternativeInvestments',
  'otherProductSocimis',
  'otherProductLifeSavingsInsurance',
  'otherProductGeneralPersonalInsurance',
]

export const getInvestmentOrderForAccountType = (
  accountType?: AccountListAccountType,
): Array<InvestmentType> => {
  switch (accountType) {
    case 'PENSION_PLAN_ACCOUNT':
      return investmentOrderForPensionPlansAccount
    case 'CASH_ACCOUNT':
      return investmentOrderForCashAccount
    default:
      return investmentOrderForSecuritiesAccount
  }
}

export interface AccountDetail {
  header: AccountDetailHeader
  details: Record<InvestmentType, AccountDetailProductSection | null>
}

export interface AccountDetailParams {
  accountId: string
}
